import { useEffect, useState } from 'react';
import PageHeader from '@/components/PageHeader.jsx';
import { fetchClientesMorosos, fetchCreditosEnRiesgo } from '@/services/clientes.js';

const formatCurrency = (value) =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(Number(value || 0));

export default function CreditosDashboard() {
  const [sucursalId, setSucursalId] = useState('1');
  const [tab, setTab] = useState('riesgo');
  const [riesgo, setRiesgo] = useState([]);
  const [morosos, setMorosos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!sucursalId) return;
      setLoading(true);
      setError('');
      try {
        const [enRiesgo, conMora] = await Promise.all([
          fetchCreditosEnRiesgo({ sucursalId }),
          fetchClientesMorosos({ sucursalId }),
        ]);
        if (!mounted) return;
        setRiesgo(enRiesgo);
        setMorosos(conMora);
      } catch (err) {
        if (mounted) setError(err instanceof Error ? err.message : 'No se pudo cargar el dashboard de créditos.');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [sucursalId]);

  const rows = tab === 'morosos' ? morosos : riesgo;

  return (
    <div className="min-h-screen bg-[rgba(var(--bg))]">
      <div className="max-w-[1400px] mx-auto px-4 py-6">
        <PageHeader title="Finanzas · Créditos" subtitle="Clientes en riesgo, excedidos y morosos por sucursal." />

        <div className="mb-4 flex flex-wrap items-center gap-3">
          <label className="text-xs font-semibold uppercase text-[rgb(var(--muted))]">Sucursal</label>
          <input
            value={sucursalId}
            onChange={(e) => setSucursalId(e.target.value)}
            className="rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--panel))] px-3 py-2 text-sm text-[rgb(var(--fg))]"
          />
          <button
            type="button"
            onClick={() => setTab('riesgo')}
            className={`rounded-xl px-3 py-2 text-sm font-semibold border ${
              tab === 'riesgo'
                ? 'border-[rgb(var(--primary))] text-[rgb(var(--primary))] bg-[rgba(var(--primary),0.08)]'
                : 'border-[rgb(var(--border))] text-[rgb(var(--muted))]'
            }`}
          >
            En riesgo / Excedidos ({riesgo.length})
          </button>
          <button
            type="button"
            onClick={() => setTab('morosos')}
            className={`rounded-xl px-3 py-2 text-sm font-semibold border ${
              tab === 'morosos'
                ? 'border-[rgb(var(--primary))] text-[rgb(var(--primary))] bg-[rgba(var(--primary),0.08)]'
                : 'border-[rgb(var(--border))] text-[rgb(var(--muted))]'
            }`}
          >
            Morosos ({morosos.length})
          </button>
        </div>

        {loading ? (
          <div className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel))] p-6 text-sm text-[rgb(var(--muted))]">Cargando...</div>
        ) : error ? (
          <div className="rounded-2xl border border-[rgb(var(--danger))] bg-[rgba(var(--danger),0.1)] p-6 text-sm text-[rgb(var(--danger))]">{error}</div>
        ) : (
          <div className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel))] overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[rgba(var(--muted),0.08)] text-[rgb(var(--muted))]">
                <tr>
                  <th className="px-3 py-2 text-left">Cliente</th>
                  <th className="px-3 py-2 text-right">Exposure</th>
                  <th className="px-3 py-2 text-right">Límite</th>
                  <th className="px-3 py-2 text-right">% límite</th>
                  <th className="px-3 py-2 text-right">Vencido</th>
                  <th className="px-3 py-2 text-left">Estado</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={`${tab}-${row.cliente_id}`} className="border-t border-[rgb(var(--border))]">
                    <td className="px-3 py-2">{row.cliente_nombre}</td>
                    <td className="px-3 py-2 text-right">{formatCurrency(row.exposure)}</td>
                    <td className="px-3 py-2 text-right">{formatCurrency(row.limite_credito)}</td>
                    <td className="px-3 py-2 text-right">{Number(row.pct_limite || 0).toFixed(2)}%</td>
                    <td className="px-3 py-2 text-right">{formatCurrency(row.deuda_vencida)}</td>
                    <td className="px-3 py-2">{row.estado_credito}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
